import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/app/AppShell";
import AdminTabs from "@/components/app/AdminTabs";
import { useAuth } from "@/contexts/AuthContext";

const FILTERS = [
    { key: "pending", label: "Pending" },
    { key: "active", label: "Active" },
    { key: "rejected", label: "Rejected" },
    { key: "all", label: "All" },
];

const relTime = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
        return d.toLocaleDateString();
    } catch { return iso; }
};

const RoleChip = ({ role }) => (
    <span
        className={`chip ${role === "admin" ? "chip-certified" : "chip-neutral"}`}
        data-testid={`role-chip-${role}`}
    >
        {role}
    </span>
);

const StatusChip = ({ status }) => {
    const cls =
        status === "active" ? "chip-certified"
        : status === "pending" ? "chip-draft"
        : "chip-neutral";
    return (
        <span className={`chip ${cls}`} data-testid={`status-chip-${status}`}>
            {status}
        </span>
    );
};

const AdminUsersPage = () => {
    const { user } = useAuth();
    const nav = useNavigate();
    const [params, setParams] = useSearchParams();
    const filter = params.get("filter") || "pending";
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [countsAll, setCountsAll] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState("");

    const loadAll = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/users?status=all");
            setCountsAll(data);
        } catch { /* non-blocking */ }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const { data } = await api.get(`/admin/users?status=${filter}`);
            setUsers(data);
        } catch (err) {
            if (err?.response?.status === 403) {
                nav("/reports", { replace: true });
                return;
            }
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [filter, nav]);

    useEffect(() => { loadAll(); load(); }, [loadAll, load]);

    const counts = useMemo(() => {
        const c = { pending: 0, active: 0, rejected: 0, all: countsAll.length };
        countsAll.forEach((u) => { if (c[u.status] != null) c[u.status]++; });
        return c;
    }, [countsAll]);

    const doAction = async (u, action) => {
        const confirmsFor = { reject: "Reject this account?", delete: "Delete this user and all their reports? This cannot be undone.", demote: "Demote this admin?" };
        if (confirmsFor[action] && !window.confirm(confirmsFor[action])) return;
        try {
            if (action === "delete") {
                await api.delete(`/admin/users/${u.id}`);
                toast.success("User deleted", { duration: 1500 });
            } else {
                await api.post(`/admin/users/${u.id}/${action}`);
                toast.success(`${action} → ok`, { duration: 1500 });
            }
            loadAll();
            load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail?.error === "LAST_ADMIN") {
                toast.error(detail.message || "Cannot demote/delete the last admin", { duration: 2500 });
            } else {
                toast.error(formatApiError(err), { duration: 2500 });
            }
        }
    };

    // Guard: non-admin bounces
    useEffect(() => {
        if (user && user !== false && user.role !== "admin") {
            nav("/reports", { replace: true });
        }
    }, [user, nav]);

    const startEditName = (u) => {
        setEditingId(u.id);
        setEditingName(u.full_name || "");
    };
    const cancelEditName = () => {
        setEditingId(null);
        setEditingName("");
    };
    const saveEditName = async (u) => {
        const next = editingName.trim() || null;
        try {
            await api.patch(`/admin/users/${u.id}`, { full_name: next });
            toast.success("Name updated", { duration: 1500 });
            cancelEditName();
            loadAll();
            load();
        } catch (err) {
            toast.error(formatApiError(err), { duration: 2500 });
        }
    };

    const renderActions = (u) => {
        const isSelf = u.is_self;
        if (u.status === "pending") {
            return (
                <>
                    <button className="btn btn-primary btn-sm" onClick={() => doAction(u, "approve")} data-testid={`approve-${u.id}`}>Approve</button>
                    <button className="btn btn-danger btn-sm" onClick={() => doAction(u, "reject")} data-testid={`reject-${u.id}`}>Reject</button>
                </>
            );
        }
        if (u.status === "rejected") {
            return (
                <>
                    <button className="btn btn-primary btn-sm" onClick={() => doAction(u, "approve")} data-testid={`approve-${u.id}`}>Approve</button>
                    <button className="btn btn-danger btn-sm" onClick={() => doAction(u, "delete")} data-testid={`delete-${u.id}`}>Delete</button>
                </>
            );
        }
        // active
        if (u.role === "admin") {
            return (
                <>
                    <button className="btn btn-secondary btn-sm" onClick={() => doAction(u, "demote")} disabled={isSelf} data-testid={`demote-${u.id}`}>Demote to foreman</button>
                    <button className="btn btn-danger btn-sm" onClick={() => doAction(u, "delete")} disabled={isSelf} data-testid={`delete-${u.id}`}>Delete</button>
                </>
            );
        }
        return (
            <>
                <button className="btn btn-secondary btn-sm" onClick={() => doAction(u, "promote")} data-testid={`promote-${u.id}`}>Promote to admin</button>
                <button className="btn btn-danger btn-sm" onClick={() => doAction(u, "reject")} data-testid={`reject-${u.id}`}>Reject</button>
                <button className="btn btn-danger btn-sm" onClick={() => doAction(u, "delete")} data-testid={`delete-${u.id}`}>Delete</button>
            </>
        );
    };

    return (
        <AppShell>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
                <div>
                    <div className="text-[13px] uppercase tracking-wider text-slate-400">Admin</div>
                    <h1 className="text-3xl font-semibold text-slate-900 mt-1" data-testid="admin-page-title">Team</h1>
                </div>
            </div>

            <AdminTabs />

            <div className="flex flex-wrap gap-2 mb-5" data-testid="admin-filters">
                {FILTERS.map((f) => (
                    <button
                        key={f.key}
                        type="button"
                        onClick={() => setParams({ filter: f.key })}
                        className={`chip ${filter === f.key ? "chip-certified" : "chip-neutral"}`}
                        data-testid={`admin-filter-${f.key}`}
                    >
                        {f.label}
                        <span className="ml-1 opacity-70">{counts[f.key]}</span>
                    </button>
                ))}
            </div>

            {error && (
                <div className="err rounded-[10px] border border-red-200 bg-red-50/70 px-3 py-2 mb-4">{error}</div>
            )}

            {loading ? (
                <div className="glass-card text-[13px] text-slate-500">Loading…</div>
            ) : users.length === 0 ? (
                <div className="glass-card text-center py-10 text-[14px] text-slate-500">
                    No users in this filter.
                </div>
            ) : (
                <>
                {/* Desktop table */}
                <div className="glass-card !p-0 overflow-hidden desktop-only">
                    <table className="g-table" data-testid="admin-users-table">
                        <thead>
                            <tr>
                                <th style={{ width: "22%" }}>Full name</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Status</th>
                                <th>Signed up</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u.id} data-testid={`admin-row-${u.id}`}>
                                    <td>
                                        {editingId === u.id ? (
                                            <div className="flex items-center gap-1">
                                                <input
                                                    className="input !h-9 !py-1 text-[13px]"
                                                    value={editingName}
                                                    onChange={(e) => setEditingName(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") saveEditName(u);
                                                        if (e.key === "Escape") cancelEditName();
                                                    }}
                                                    autoFocus
                                                    data-testid={`fullname-input-${u.id}`}
                                                />
                                                <button
                                                    className="btn btn-primary btn-sm btn-icon"
                                                    onClick={() => saveEditName(u)}
                                                    aria-label="Save name"
                                                    data-testid={`fullname-save-${u.id}`}
                                                >
                                                    <Check size={12} strokeWidth={2.5} />
                                                </button>
                                                <button
                                                    className="btn btn-secondary btn-sm btn-icon"
                                                    onClick={cancelEditName}
                                                    aria-label="Cancel"
                                                    data-testid={`fullname-cancel-${u.id}`}
                                                >
                                                    <X size={12} strokeWidth={2.5} />
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                className="inline-flex items-center gap-1.5 text-[14px] text-slate-900 hover:text-emerald-800 text-left"
                                                onClick={() => startEditName(u)}
                                                data-testid={`fullname-edit-${u.id}`}
                                            >
                                                <span className={u.full_name ? "" : "italic text-slate-400"}>
                                                    {u.full_name || "Unnamed"}
                                                </span>
                                                <Pencil size={12} strokeWidth={2} className="text-slate-400" />
                                            </button>
                                        )}
                                    </td>
                                    <td>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[14px] text-slate-700">{u.email}</span>
                                            {u.is_self && <span className="chip chip-neutral" data-testid={`you-chip-${u.id}`}>You</span>}
                                        </div>
                                    </td>
                                    <td><RoleChip role={u.role} /></td>
                                    <td><StatusChip status={u.status} /></td>
                                    <td className="text-[13px] text-slate-500">{relTime(u.created_at)}</td>
                                    <td className="text-right">
                                        <div className="inline-flex gap-2 justify-end">{renderActions(u)}</div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile stacked cards */}
                <div className="mobile-only flex flex-col gap-3">
                    {users.map((u) => (
                        <div key={u.id} className="glass-card !py-4" data-testid={`admin-card-${u.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    {editingId === u.id ? (
                                        <div className="flex items-center gap-1 mb-1">
                                            <input
                                                className="input !h-9 !py-1 text-[14px]"
                                                value={editingName}
                                                onChange={(e) => setEditingName(e.target.value)}
                                                autoFocus
                                                data-testid={`fullname-input-m-${u.id}`}
                                            />
                                            <button
                                                className="btn btn-primary btn-sm btn-icon"
                                                onClick={() => saveEditName(u)}
                                                aria-label="Save name"
                                                data-testid={`fullname-save-m-${u.id}`}
                                            >
                                                <Check size={12} strokeWidth={2.5} />
                                            </button>
                                            <button
                                                className="btn btn-secondary btn-sm btn-icon"
                                                onClick={cancelEditName}
                                                aria-label="Cancel"
                                            >
                                                <X size={12} strokeWidth={2.5} />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => startEditName(u)}
                                            className="inline-flex items-center gap-1.5 text-[15px] font-medium text-slate-900 mb-0.5"
                                            data-testid={`fullname-edit-m-${u.id}`}
                                        >
                                            <span className={u.full_name ? "" : "italic text-slate-400"}>
                                                {u.full_name || "Unnamed"}
                                            </span>
                                            <Pencil size={12} strokeWidth={2} className="text-slate-400" />
                                        </button>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <span className="text-[13px] text-slate-500 truncate">{u.email}</span>
                                        {u.is_self && <span className="chip chip-neutral">You</span>}
                                    </div>
                                    <div className="mt-1 flex items-center gap-2">
                                        <RoleChip role={u.role} />
                                        <StatusChip status={u.status} />
                                    </div>
                                    <div className="text-[12px] text-slate-500 mt-1">Signed up {relTime(u.created_at)}</div>
                                </div>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">{renderActions(u)}</div>
                        </div>
                    ))}
                </div>
                </>
            )}
        </AppShell>
    );
};

export default AdminUsersPage;
